import Head from "next/head";
import {useRouter} from "next/router";
import {MouseEvent, useEffect, useRef} from "react";

import {HtmlPack as HtmlPackData} from "@/lib/html-pack";

// Scripts are shared between packs and survive client-side navigation.
const loaded = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const existing = loaded.get(src);
  if (existing) return existing;

  const pending = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(script);
  });

  loaded.set(src, pending);
  return pending;
}

/** Resolves "SW.init" against window. */
function resolveInit(path: string | null): ((root: Element) => void) | null {
  if (!path) return null;
  const target = path.split('.').reduce<any>(
    (carrier, key) => (carrier === undefined || carrier === null ? carrier : carrier[key]),
    window,
  );
  return typeof target === 'function' ? target : null;
}

interface HtmlPackProps {
  pack: HtmlPackData;
  className?: string;
}

/**
 * Renders one HTML pack: the fragment as authored, its stylesheets, and scripts.
 */
export function HtmlPack({pack, className}: HtmlPackProps) {
  const router = useRouter();
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pack?.js?.length && !pack?.init) return;
    let mounted = true;

    // Scripts load in the order they are listed; a pack may depend on it.
    const ready = pack.js.reduce(
      (chain, src) => chain.then(() => loadScript(src)),
      Promise.resolve(),
    );

    ready
      .then(() => {
        // The route may have changed while the scripts were downloading.
        if (!mounted || !container.current) return;
        const init = resolveInit(pack.init);
        const root = container.current.firstElementChild;
        if (init && root) init(root);
      })
      .catch((error) => console.error('[html-pack]', error));

    return () => {
      mounted = false;
    };
  }, [pack]);

  if (!pack?.html) return null;

  // Links inside the fragment are plain anchors.
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const anchor = (event.target as HTMLElement)?.closest?.('a');
    const href = anchor?.getAttribute('href');
    if (!href || !href.startsWith('/') || anchor?.getAttribute('target') === '_blank') return;
    event.preventDefault();
    router.push(href);
  };

  return (
    <>
      <Head>
        {pack.css.map((href) => (
          <link key={href} rel="stylesheet" href={href}/>
        ))}
      </Head>
      <div
        ref={container}
        className={className}
        onClick={onClick}
        dangerouslySetInnerHTML={{__html: pack.html}}
      />
    </>
  );
}
